/**
 * TilePool - displays the middle pool of available tiles
 */

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { Tile } from './Tile';
import { useGameStore } from '../../stores/gameStore';

const EMPTY_MIDDLE: string[] = [];

interface TilePoolProps {
  onTileClick?: (letter: string, index: number) => void;
  selectedTiles?: Set<number>;
}

export function TilePool({ onTileClick, selectedTiles }: TilePoolProps) {
  const middle = useGameStore((s) => s.gameState?.middle ?? EMPTY_MIDDLE);
  const isStealMode = useGameStore((s) => s.selectedWordsToSteal.size > 0);
  const typingHighlightIndices = useGameStore((s) => s.typingHighlightIndices);
  const lastFlip = useGameStore((s) => s.lastFlip);

  // Stable keys: letter + occurrence count. Removing claimed tiles doesn't
  // shift the keys of unrelated letters, so old tiles never remount/re-animate.
  const tileKeys = useMemo(() => {
    const seen = new Map<string, number>();
    return middle.map((letter) => {
      const n = seen.get(letter) ?? 0;
      seen.set(letter, n + 1);
      return `${letter}-${n}`;
    });
  }, [middle]);

  // Only the tile produced by the most recent flip counts as "new" - after a
  // claim the middle shrinks so lastFlip no longer matches.
  const newTileIndex =
    lastFlip &&
    lastFlip.middleLength === middle.length &&
    middle[middle.length - 1] === lastFlip.letter
      ? middle.length - 1
      : -1;

  const hasHighlights = typingHighlightIndices.size > 0;

  return (
    <div
      className={clsx(
        "middle-pool transition-all duration-200",
        isStealMode && "ring-2 ring-orange-400 ring-offset-2 bg-orange-50/50 dark:bg-orange-900/20"
      )}
      data-tile-pool
    >
      {/* Header with tile count and steal mode indicator */}
      <div className="flex flex-col xs:flex-row items-start xs:items-center justify-between gap-1 xs:gap-2 px-2 sm:px-4 pt-2 sm:pt-3 pb-1">
        <span className={clsx(
          "text-xs font-medium uppercase tracking-wide",
          hasHighlights ? "text-blue-600 dark:text-blue-400" :
          isStealMode ? "text-orange-600 dark:text-orange-400" : "text-slate-500"
        )}>
          {hasHighlights ? `Using ${typingHighlightIndices.size} tile${typingHighlightIndices.size !== 1 ? 's' : ''}` :
           isStealMode ? "Add letters from here ↓" : "Letters"}
        </span>
        {middle.length > 0 && (
          <motion.span
            key={middle.length}
            initial={{ scale: 1.3 }}
            animate={{ scale: 1 }}
            className={clsx(
              "text-xs font-bold px-2 py-0.5 rounded-full",
              isStealMode
                ? "text-orange-700 bg-orange-100 dark:text-orange-300 dark:bg-orange-900/50"
                : "text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/50"
            )}
          >
            {middle.length} tile{middle.length !== 1 ? 's' : ''}
          </motion.span>
        )}
      </div>

      {/* Tiles or empty state */}
      {middle.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-8 text-slate-400">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="text-4xl mb-2 opacity-50">🎯</div>
            <p className="text-sm">No tiles yet</p>
            <p className="text-xs text-slate-300 dark:text-slate-500 mt-1">Flip a tile to start!</p>
          </motion.div>
        </div>
      ) : (
        <div
          className="tile-pool-scatter"
          role="list"
          aria-label="Available letter tiles"
        >
          {middle.map((letter, index) => (
            <div
              key={tileKeys[index]}
              className="tile-pool-item"
              role="listitem"
            >
              <Tile
                letter={letter}
                isNew={index === newTileIndex}
                onClick={onTileClick ? () => onTileClick(letter, index) : undefined}
                selected={selectedTiles?.has(index)}
                highlighted={typingHighlightIndices.has(index)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
